import React, { useState } from 'react';
import { generateSelfRelianceGuide } from '../services/geminiService';
import { SelfRelianceGuide } from '../types';
import { UserCheck, RefreshCw, Star, ArrowRight } from 'lucide-react';
import { useUser } from '../context/UserContext';

const HappyReliance: React.FC = () => {
  const [situation, setSituation] = useState('');
  const [guide, setGuide] = useState<SelfRelianceGuide | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handleGuide = async () => {
    if (!situation.trim()) return;
    setLoading(true);
    setGuide(null);
    try {
      const data = await generateSelfRelianceGuide(situation);
      setGuide(data);
      addXp(40, 'Built Self-Reliance');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-emerald-50 text-emerald-600 rounded-full mb-4">
          <UserCheck size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Self-Reliance Builder</h1>
        <p className="text-nunchi-600">
          True independence is not needing praise to feel value. <br/>
          Shift from "What will they think of me?" to "How can I contribute?"
        </p>
      </header>

      {!guide && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                Where do you feel dependent on others' approval?
            </label>
            <textarea
              value={situation}
              onChange={(e) => setSituation(e.target.value)}
              placeholder="e.g., I only feel good when my boss praises me, I feel worthless when my partner is busy, I need likes on social media..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-emerald-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleGuide}
              disabled={!situation.trim()}
              className="w-full bg-emerald-700 text-white py-4 rounded-xl font-bold hover:bg-emerald-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <UserCheck size={18} /> Build Independence
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-emerald-700">Finding your inner worth...</p>
        </div>
      )}

      {guide && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-emerald-900 text-emerald-50 p-8 rounded-2xl shadow-xl relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                 
                 <div className="mb-6">
                     <h3 className="text-emerald-300 uppercase tracking-widest text-xs font-bold mb-2">Dependency Check</h3>
                     <p className="text-lg font-serif text-white">{guide.dependencyCheck}</p>
                 </div>

                 <div className="mb-6 border-l-4 border-emerald-500 pl-4">
                     <h3 className="text-emerald-300 uppercase tracking-widest text-xs font-bold mb-2">Mindset Shift</h3>
                     <p className="text-emerald-100 italic">"{guide.mindset}"</p>
                 </div>

                 <div className="bg-white/10 p-4 rounded-xl flex items-start gap-4">
                     <Star className="flex-shrink-0 text-emerald-300 mt-1" size={24} />
                     <div>
                         <h3 className="text-emerald-300 uppercase tracking-widest text-xs font-bold mb-1">Your Challenge</h3>
                         <p className="font-bold text-white">{guide.challenge}</p>
                     </div>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setGuide(null); setSituation(''); }}
                  className="text-emerald-600 hover:text-emerald-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Analyze Another Situation
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default HappyReliance;